import React, { forwardRef } from 'react';
import cx from 'classnames';
import PositionIcon, { SLOT_TO_POSITION, POSITION_CN } from '../europe/PositionIcon';
import css from './SeriesReportCard.module.scss';

// SeriesReportCard：一张图复盘整个 BO 系列
// - 固定尺寸 1920×1080（16:9 HD）
// - 离屏渲染，html2canvas 截图后是一张能直发微信群的总结图
// - 数据源：useBOArchive 的 archive 对象（含 slim champion）
//
// 注意 champion 字段：archive 里的 champion 是瘦身版（id/idName/name）
// 立绘 URL 由 ddragonImg 工具拼接（不依赖 useChampionData）
//
// 布局：
//   ┌──────────────────────────────────────────┐
//   │ Header: BO5 · 蓝3-2红 · 2026-05-26      │
//   ├──────────────────────────────────────────┤
//   │ Game 1 [BLUE 5 picks | 5 bans] vs [RED ...] │
//   │ Game 2 ...                                 │
//   │ ...                                        │
//   ├──────────────────────────────────────────┤
//   │ 全系列出场统计 · Fearless 池 · footer    │
//   └──────────────────────────────────────────┘

// 取 champion 头像：优先用 archive 存好的 squareImg（含动态版本号），
// fallback 用版本无关的 loading splash（也能显示，只是非头像）
const championImg = (slim) => {
  if (!slim) return '';
  if (slim.squareImg) return slim.squareImg;
  if (slim.idName) {
    return `https://ddragon.leagueoflegends.com/cdn/img/champion/loading/${slim.idName}_0.jpg`;
  }
  return '';
};

const SeriesReportCard = forwardRef(({ archive, exportedAtFormatted }, ref) => {
  if (!archive) {
    return (
      <div ref={ref} className={css.Card}>
        <div style={{ color: '#888', padding: 80 }}>暂无 BO 存档数据</div>
      </div>
    );
  }

  const { mode, totalGames, scores = { blue: 0, red: 0 },
          blueName = '蓝方', redName = '红方',
          fearlessEnabled, games = [], label } = archive;

  // 衍生：全系列英雄出场统计（picks 计数）
  const stats = buildSeriesStats(games);

  return (
    <div ref={ref} className={css.Card}>
      {/* ===== Header ===== */}
      <div className={css.Header}>
        <div className={css.HeaderLeft}>
          <div className={css.Brand}>LoL Coach · 系列复盘</div>
          <div className={css.Title}>{label}</div>
        </div>
        <div className={css.HeaderCenter}>
          <div className={css.ScoreRow}>
            <span className={css.ScoreTeam_blue}>{blueName}</span>
            <span className={css.ScoreNum}>
              <strong className={css.ScoreNumBlue}>{scores.blue}</strong>
              <span className={css.ScoreDash}>:</span>
              <strong className={css.ScoreNumRed}>{scores.red}</strong>
            </span>
            <span className={css.ScoreTeam_red}>{redName}</span>
          </div>
          <div className={css.ScoreMeta}>
            {mode || `BO${totalGames}`} · {fearlessEnabled ? 'Fearless ON' : 'Fearless OFF'}
          </div>
        </div>
        <div className={css.HeaderRight}>
          <div className={css.HeaderExportTime}>{exportedAtFormatted}</div>
          <div className={css.HeaderSeriesProgress}>
            {games.length}/{totalGames} 局
          </div>
        </div>
      </div>

      {/* ===== Games 主体 ===== */}
      <div className={cx(css.GamesArea, css[`GamesArea_count_${games.length}`])}>
        {games.map((g, idx) => (
          <GameRow key={g.gameIndex} game={g} idx={idx} blueName={blueName} redName={redName} />
        ))}
        {games.length === 0 && (
          <div className={css.EmptyHint}>本 BO 没有已完成的局</div>
        )}
      </div>

      {/* ===== 全系列统计 ===== */}
      <div className={css.StatsRow}>
        <SeriesPickedList stats={stats} side="blue" name={blueName} />
        <SeriesPickedList stats={stats} side="red"  name={redName} />
      </div>

      <div className={css.Footer}>
        Generated by lol-pick-ban-ui · Coach Mode · v1
      </div>
    </div>
  );
});

// ---------- 一局：picks 大头像 + bans 小头像 + 胜负条 ----------

const GameRow = ({ game, idx, blueName, redName }) => {
  const blueWin = game.winner === 'blue';
  const redWin  = game.winner === 'red';
  return (
    <div className={cx(
      css.GameRow,
      blueWin && css.GameRow_blueWin,
      redWin && css.GameRow_redWin,
    )}>
      <div className={css.GameTag}>
        <span className={css.GameTagNum}>G{idx + 1}</span>
        <span className={cx(css.GameTagFirst, css[`GameTagFirst_${game.firstSide}`])}>
          {game.firstSide === 'blue' ? '蓝先手' : '红先手'}
        </span>
      </div>

      <TeamHalf
        side="blue"
        name={blueName}
        picks={game.bluePicks}
        bans={game.blueBans}
        isWin={blueWin}
      />

      <div className={css.GameVs}>
        <span className={css.GameVsLine} />
        <span className={css.GameVsText}>VS</span>
        <span className={css.GameVsLine} />
      </div>

      <TeamHalf
        side="red"
        name={redName}
        picks={game.redPicks}
        bans={game.redBans}
        isWin={redWin}
        reverse
      />
    </div>
  );
};

const TeamHalf = ({ side, name, picks = [], bans = [], isWin, reverse }) => (
  <div className={cx(css.TeamHalf, css[`TeamHalf_${side}`], reverse && css.TeamHalf_reverse)}>
    {isWin && (
      <span className={cx(css.WinBadge, css[`WinBadge_${side}`])}>WIN</span>
    )}
    <div className={css.PicksMini}>
      {[0,1,2,3,4].map((i) => {
        const c = picks[i];
        return (
          <div key={i} className={cx(css.PickMini, css[`PickMini_${side}`], c && css.PickMiniFilled)}>
            {c ? (
              <img
                src={championImg(c)}
                alt={c.name}
                crossOrigin="anonymous"
                width="78" height="78"
                className={css.PickMiniImg}
              />
            ) : (
              <span className={css.PickMiniEmpty}>—</span>
            )}
            <span className={css.PickMiniPos}>
              <PositionIcon position={SLOT_TO_POSITION[i]} size={11} />
            </span>
          </div>
        );
      })}
    </div>
    <div className={css.BansMini}>
      {[0,1,2,3,4].map((i) => {
        const c = bans[i];
        return (
          <div key={i} className={cx(css.BanMini, css[`BanMini_${side}`], c && css.BanMiniFilled)}>
            {c ? (
              <img
                src={championImg(c)}
                alt={c.name}
                crossOrigin="anonymous"
                width="30" height="30"
                className={css.BanMiniImg}
              />
            ) : (
              <span className={css.BanMiniDot}>·</span>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

// ---------- 全系列统计：每队各自的高频出场英雄 ----------

const buildSeriesStats = (games) => {
  // championId -> { blue: count, red: count, name, slim }
  const map = new Map();
  const bump = (slim, side) => {
    if (!slim || !slim.id) return;
    const cur = map.get(slim.id) || { blue: 0, red: 0, name: slim.name, slim };
    cur[side] += 1;
    map.set(slim.id, cur);
  };
  games.forEach((g) => {
    (g.bluePicks || []).forEach((c) => bump(c, 'blue'));
    (g.redPicks  || []).forEach((c) => bump(c, 'red'));
  });
  return map;
};

const SeriesPickedList = ({ stats, side, name }) => {
  const arr = Array.from(stats.values())
    .filter((s) => (s[side] || 0) > 0)
    .sort((a, b) => b[side] - a[side])
    .slice(0, 10);
  return (
    <div className={cx(css.StatsBlock, css[`StatsBlock_${side}`])}>
      <div className={css.StatsTitle}>
        <span className={css.StatsTeam}>{name}</span>
        <span className={css.StatsLabel}>全系列出场 TOP {arr.length}</span>
      </div>
      <div className={css.StatsList}>
        {arr.length === 0 && <span className={css.StatsEmpty}>—</span>}
        {arr.map((s) => (
          <div key={s.slim.id} className={cx(css.StatsItem, css[`StatsItem_${side}`])}>
            <img
              src={championImg(s.slim)}
              alt={s.name}
              crossOrigin="anonymous"
              width="34" height="34"
              className={css.StatsImg}
            />
            <span className={css.StatsName}>{s.name}</span>
            <span className={css.StatsCount}>×{s[side]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SeriesReportCard;
